import React, { useEffect, useState } from "react";
import { Loader2, ExternalLink } from "lucide-react";
import { zohoApi } from "../../lib/api";

/**
 * ZohoEmailTab — sidebar tab inside ChatsPage that lists every Zoho Desk
 * ticket linked to the lead (via /link-lead) plus any tickets matched by
 * the lead's email or phone. Each ticket is expandable to inline its
 * conversation threads.
 *
 * Extracted from ChatsPage.jsx Apr 2026 as part of the refactor sprint.
 */
const ZohoEmailTab = ({ lead }) => {
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null); // ticket id → threads
  const [threadsMap, setThreadsMap] = useState({});
  const [threadsLoading, setThreadsLoading] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchTickets = async () => {
      if (!lead?.id) return;
      setLoading(true); setError(null);
      const email = lead.customer_email;
      const phone = lead.customer_phone || lead.phone;
      try {
        // Fetch in parallel: tickets explicitly linked (via /link-lead) +
        // email/phone-matched tickets from Zoho's contact lookup.
        const [linkedRes, contactRes] = await Promise.all([
          zohoApi.ticketsForLead(lead.id).catch(() => ({ data: { tickets: [] } })),
          (email || phone)
            ? zohoApi.ticketsByContact({ email, phone, limit: 15 }).catch(() => ({ data: { tickets: [] } }))
            : Promise.resolve({ data: { tickets: [] } }),
        ]);
        const linked = (linkedRes.data?.tickets || []).map((t) => ({ ...t, _linked: true }));
        const linkedIds = new Set(linked.map((t) => t.id));
        const byContact = (contactRes.data?.tickets || []).filter((t) => !linkedIds.has(t.id));
        const combined = [...linked, ...byContact];
        if (!cancelled) {
          setTickets(combined);
          if (combined.length === 0 && !email && !phone) {
            setError("This lead has no email or phone to match against Zoho tickets");
          }
        }
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || "Failed to load tickets");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchTickets();
    return () => { cancelled = true; };
  }, [lead?.id, lead?.customer_email, lead?.customer_phone, lead?.phone]);

  const toggleThreads = async (t) => {
    if (expanded === t.id) { setExpanded(null); return; }
    setExpanded(t.id);
    if (!threadsMap[t.id]) {
      setThreadsLoading(t.id);
      try {
        const res = await zohoApi.ticketThreads(t.id, 10);
        setThreadsMap((prev) => ({ ...prev, [t.id]: res.data.threads || [] }));
      } catch {
        setThreadsMap((prev) => ({ ...prev, [t.id]: [] }));
      } finally {
        setThreadsLoading(null);
      }
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-8"><Loader2 size={18} className="animate-spin text-[--text-muted]" /></div>;
  }

  if (error) {
    return (
      <div className="p-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded" data-testid="zoho-tab-error">
        {error}
        {error.includes("not authorized") && <p className="mt-2">Go to <b>Settings → Zoho Desk</b> to set up.</p>}
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="zoho-email-tickets">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[11px] font-bold text-[--text-muted] uppercase tracking-wider">Zoho Desk Emails</p>
        <span className="text-[10px] text-[--text-muted]">{tickets.length} found</span>
      </div>
      {tickets.length === 0 && (
        <div className="p-3 bg-gray-50 rounded text-xs text-gray-500 text-center">
          No email tickets in Zoho for this customer
        </div>
      )}
      {tickets.map((t) => {
        const isOpen = expanded === t.id;
        const statusColor =
          t.statusType === "Open" ? "bg-green-50 text-green-700 border-green-200" :
          t.statusType === "Closed" ? "bg-gray-50 text-gray-600 border-gray-200" :
          "bg-amber-50 text-amber-700 border-amber-200";
        return (
          <div key={t.id} className="border border-[--border] rounded-lg overflow-hidden hover:border-[--accent-soft]/60 transition-colors" data-testid={`zoho-ticket-${t.ticketNumber}`}>
            <button onClick={() => toggleThreads(t)} className="w-full p-2.5 text-left hover:bg-gray-50 transition-colors">
              <div className="flex items-start justify-between gap-2 mb-1">
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] font-bold text-[--text-muted]">#{t.ticketNumber}</span>
                  {t._linked && (
                    <span className="text-[8px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-200 rounded-full px-1.5 py-0.5 inline-flex items-center gap-0.5" data-testid={`linked-badge-${t.ticketNumber}`}>
                      🔗 LINKED
                    </span>
                  )}
                </div>
                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full border ${statusColor}`}>{t.status}</span>
              </div>
              <p className="text-xs font-medium text-[--text-primary] line-clamp-2 mb-1" title={t.subject}>{t.subject}</p>
              <div className="flex items-center justify-between text-[10px] text-[--text-muted]">
                <span>{t.createdTime?.slice(0, 10)}</span>
                <div className="flex items-center gap-2">
                  <a href={`/emails?ticket=${t.id}`} onClick={(e) => e.stopPropagation()} className="text-[--accent] hover:underline flex items-center gap-0.5" data-testid={`open-in-crm-${t.ticketNumber}`}>
                    Open in CRM
                  </a>
                  {t.web_url && (
                    <a href={t.web_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-[--text-muted] hover:text-[--accent] flex items-center gap-0.5">
                      Zoho <ExternalLink size={9} />
                    </a>
                  )}
                </div>
              </div>
            </button>
            {isOpen && (
              <div className="border-t border-[--border] bg-gray-50 p-2 space-y-2 max-h-64 overflow-y-auto">
                {threadsLoading === t.id && <Loader2 size={14} className="animate-spin text-[--text-muted] mx-auto" />}
                {threadsMap[t.id]?.length === 0 && <p className="text-[10px] text-[--text-muted] text-center py-2">No threads yet</p>}
                {threadsMap[t.id]?.map((th) => (
                  <div key={th.id} className={`p-2 rounded text-[11px] ${th.direction === "out" ? "bg-blue-50 ml-4" : "bg-white mr-4 border border-gray-200"}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-bold text-[10px] text-[--text-secondary]">{th.author_name || th.author_email || "Unknown"}</span>
                      <span className="text-[9px] text-[--text-muted]">{th.createdTime?.slice(0, 16)?.replace("T", " ")}</span>
                    </div>
                    <div className="text-[--text-primary] line-clamp-4">
                      {(th.content || th.summary || "").replace(/<[^>]*>/g, " ").slice(0, 400)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ZohoEmailTab;
